import React from "react";
import { ApolloProvider } from "react-apollo";
import { debounce } from "lodash";
import PropTypes from "prop-types";

import GlobalState from "../../../../../shared/utilities/globalState";
import {
  boundaryCreatedGA,
  contactConfirmationGA,
  emailCaptureGA,
  passwordRequestedGA,
  resetPasswordConfirmationGA,
  resetPasswordGA,
  setPasswordGA,
  verificationSentGA
} from "../../analytics/ga";
import apolloClient from "../../connection/apolloClient";
import CREATE_BOUNDARY from "../../mutations/create_boundary";
import { triggerConfirmationModalGA } from "../Analytics/analytics";
import Toast from "../Toast";
import ContactConfirmation from "./scenarios/ContactConfirmation";
import EmailCapture from "./scenarios/EmailCapture";
import PasswordRequested from "./scenarios/PasswordRequested";
import PasswordRequiredToRegister from "./scenarios/PasswordRequiredToRegister";
import RegisterIndustryConfirmation from "./scenarios/RegisterIndustryConfirmation";
import ResetPassword from "./scenarios/ResetPassword";
import ResetPasswordConfirmation from "./scenarios/ResetPasswordConfirmation";
import SetPassword from "./scenarios/SetPassword";
import ShareConfirmation from "./scenarios/ShareConfirmation";
import VerificationSent from "./scenarios/VerificationSent";
import {
  AuthModal,
  AuthModalWrap,
  CloseButton,
  ErrorText,
  ModalContent
} from "./styled";

class RouteLogin extends React.Component {
  static defaultProps = {
    showGoogle: false,
    siteKey: ""
  };

  static propTypes = {
    showGoogle: PropTypes.bool,
    siteKey: PropTypes.string
  };

  state = {
    form: {
      data: {
        agent: "",
        agentUrl: "",
        confirmationText: "",
        email: "",
        logoUrl: "",
        origin: "",
        sourceGroup: ""
      },
      email: "",
      origin: "signin",
      password: ""
    },
    formError: null,
    isModalOpen: false,
    routeLoginCompleted: false,
    scenario: "EmailCapture",
    windowWidth: window.innerWidth
  };

  componentDidMount() {
    this.initScenario();
    this.setWindowWidth();
  }

  setWindowWidth = () => {
    const events = ["resize", "load"];

    events.forEach(event => {
      window.addEventListener(
        event,
        debounce(() => this.setState({ windowWidth: window.innerWidth }), 10)
      );
    });
  };

  getScenario = () => {
    const formProps = {
      form: this.state.form,
      formError: this.state.formError,
      handleCloseModal: this.handleCloseModal,
      handleInputChange: this.handleInputChange,
      handleMutationError: this.handleMutationError,
      handleRenderError: this.handleRenderError,
      isModalOpen: this.state.isModalOpen,
      setScenario: this.setScenario,
      setTheme: this.setTheme(),
      topPosition: this.state.windowWidth > 660 ? "120px" : "50px"
    };

    let scenario = (
      <EmailCapture
        handleCloseModal={this.handleCloseModal}
        handleCreateBoundary={this.handleCreateBoundary}
        handleRedirectTo={this.handleRedirectTo}
        handleSuggestedEmailClick={this.handleSuggestedEmailClick}
        showGoogle={this.props.showGoogle}
        siteKey={this.props.siteKey}
        {...formProps}
      />
    );

    if (this.state.scenario === "PasswordRequiredToRegister") {
      scenario = (
        <PasswordRequiredToRegister
          handleCloseModal={this.handleCloseModal}
          {...formProps}
        />
      );
    } else if (this.state.scenario === "PasswordRequested") {
      scenario = (
        <PasswordRequested
          handleCloseModal={this.handleCloseModal}
          handleCreateBoundary={this.handleCreateBoundary}
          handleRedirectTo={this.handleRedirectTo}
          {...formProps}
        />
      );
    } else if (this.state.scenario === "ResetPassword") {
      scenario = (
        <ResetPassword
          handleCloseModal={this.handleCloseModal}
          {...formProps}
        />
      );
    } else if (this.state.scenario === "VerificationSent") {
      scenario = (
        <VerificationSent
          email={this.state.form.email}
          handleCloseModal={this.handleCloseModal}
          scenario={this.state.scenario}
        />
      );
    } else if (this.state.scenario === "SetPassword") {
      scenario = (
        <SetPassword handleCloseModal={this.handleCloseModal} {...formProps} />
      );
    } else if (this.state.scenario === "ResetPasswordConfirmation") {
      scenario = (
        <ResetPasswordConfirmation
          {...formProps}
          scenario={this.state.scenario}
        />
      );
    } else if (this.state.scenario === "RegisterIndustryConfirmation") {
      scenario = (
        <RegisterIndustryConfirmation
          email={this.state.form.email}
          handleCloseModal={this.handleCloseModal}
          scenario={this.state.scenario}
        />
      );
    } else if (
      ["ContactConfirmation", "ShareConfirmation"].includes(this.state.scenario)
    ) {
      const mapping = {
        ContactConfirmation,
        ShareConfirmation
      };
      const Confirmation = mapping[this.state.scenario];
      scenario = (
        <Confirmation
          data={this.state.form.data}
          onRouteLoginCompleted={this.handleRouteLoginCompleted}
          scenario={this.state.scenario}
          {...formProps}
        />
      );
    }

    return scenario;
  };

  getToast = () => {
    if (GlobalState.has("toast")) {
      const { icon, message, timed } = GlobalState.get("toast");

      // GA start
      localStorage.setItem(
        "confirmationModalScenario",
        icon === "key" ? "setPassword" : "login"
      );

      if (GlobalState.has("analyticsData")) {
        if (GlobalState.get("analyticsData").source === "email") {
          const analyticsData = GlobalState.get("analyticsData");
          localStorage.setItem("analytics", JSON.stringify(analyticsData));
        }
      }

      // GA end
      return (
        <Toast
          icon={icon}
          message={message}
          onToastClose={this.confirmationCloseGA}
          timed={timed}
        />
      );
    }

    return null;
  };

  setScenario = (scenario, form = {}) => {
    const scenarioState = {
      scenario,
      formError: null,
      form: { ...this.state.form, ...form }
    };

    if (scenario === "EmailCapture") {
      scenarioState.form = {
        ...this.state.form,
        email: ""
      };
    } else if (scenario === "ResetPassword") {
      scenarioState.form = {
        ...this.state.form,
        email: "",
        password: ""
      };
    } else if (scenario === "PasswordRequested") {
      scenarioState.form = {
        ...this.state.form,
        ...form,
        password: ""
      };
    }
    this.setState(scenarioState);
  };

  setTheme = () => {
    if (
      this.state.windowWidth > 660 ||
      [
        "ContactConfirmation",
        "RegisterIndustryConfirmation",
        "ResetPasswordConfirmation",
        "ShareConfirmation",
        "VerificationSent"
      ].includes(this.state.scenario)
    ) {
      return "lightBox";
    }

    return "default";
  };

  // GA start
  confirmationCloseGA = () => {
    triggerConfirmationModalGA("close");
  };
  // GA end

  initScenario = () => {
    if (GlobalState.get("scenario") === "SetPassword") {
      this.setScenario("SetPassword");
      this.setState({ isModalOpen: true });
    }

    GlobalState.set("triggerScenario", (scenario, data = {}) => {
      this.setState(prevState => ({
        form: {
          ...prevState.form,
          data
        },
        isModalOpen: true,
        scenario
      }));
    });
  };

  initialState = () => {
    this.setState({
      isModalOpen: false,
      form: {
        data: {},
        email: "",
        origin: "signin",
        password: ""
      },
      formError: null,
      scenario: "EmailCapture",
      windowWidth: window.innerWidth
    });
  };

  // GA start
  closeModalGA = scenario => {
    const { flow, source } =
      JSON.parse(localStorage.getItem("analytics")) || "";

    switch (scenario) {
      case "ResetPassword":
        resetPasswordGA.forgotPasswordModalIsClosed(source, flow);
        break;
      case "PasswordRequested":
        passwordRequestedGA.enterPasswordModalIsClosed(source, flow);
        break;
      case "SetPassword":
        setPasswordGA.setPasswordModalIsClosed("email", "freepass");
        break;
      case "VerificationSent":
        verificationSentGA.welcomeBackModalClosed(source);
        break;
      case "ResetPasswordConfirmation":
        resetPasswordConfirmationGA.checkEmailModalIsClosed(source, flow);
        break;
      case "PasswordRequiredToRegister":
        setPasswordGA.setPasswordModalIsClosed(source, "industry");
        break;
      case "ContactConfirmation":
        contactConfirmationGA.modalIsClosed(source, flow);
        break;
      default:
        emailCaptureGA.signInCloseModal(source);
        break;
    }
  };
  // GA end

  handleCloseModal = () => {
    this.initialState();

    // GA start
    this.closeModalGA(this.state.scenario);
    // GA end

    const triggerScenario = GlobalState.get("triggerScenario");

    if (typeof triggerScenario === "function") {
      GlobalState.get("callbacks").accountCreated = undefined;
      GlobalState.get("callbacks").login = undefined;
      GlobalState.get("callbacks").passwordRequiredToRegister = undefined;
    }

    if (
      ["ContactConfirmation", "ShareConfirmation"].includes(
        this.state.scenario
      ) &&
      this.state.routeLoginCompleted
    ) {
      window.location.reload();
    }
  };

  handleInputChange = (value, event) => {
    this.updateFormField(event.target.name, value);
  };

  handleSuggestedEmailClick = value => this.updateFormField("email", value);

  handleRouteLoginCompleted = () => {
    this.setState({ routeLoginCompleted: true });
  };

  updateFormField = (field, value) => {
    this.setState(prevState => ({
      form: {
        ...prevState.form,
        [field]: value.trim()
      },
      formError: null
    }));
  };

  handleMutationError = data => {
    this.setState({ formError: data.graphQLErrors[0].message });
  };

  handleRenderError = () => {
    if (this.state.formError) {
      return (
        <ErrorText id="err-msg" role="alert" type="sm">
          {this.state.formError}
        </ErrorText>
      );
    }

    return null;
  };

  handleCreateBoundary = () => {
    const { ...boundary } = this.state.form.data.boundary;
    localStorage.removeItem("createdBoundary");
    localStorage.setItem(
      "createdBoundary",
      JSON.stringify({
        encoded_boundary: boundary.encoded_boundary,
        boundary_label: boundary.boundary_label
      })
    );

    const result = apolloClient.mutate({
      mutation: CREATE_BOUNDARY,
      variables: {
        boundary: JSON.stringify({
          encoded_boundary: boundary.encoded_boundary,
          boundary_label: boundary.boundary_label
        })
      }
    });

    result.then(response => {
      boundaryCreatedGA(
        GlobalState.get("analyticsData").source,
        response.data.create_boundary.analytics_data.flow
      );
      window.location.reload();
    });
  };

  handleRedirectTo = redirectTo => {
    if (redirectTo) {
      window.location.href = redirectTo;
    } else {
      window.location.reload();
    }
  };

  render() {
    const dialogStyles = {};
    const theme = this.setTheme();

    if (theme === "lightBox") {
      dialogStyles.top = this.state.windowWidth > 660 ? "120px" : "60px";
    }

    return (
      <ApolloProvider client={apolloClient}>
        <AuthModalWrap scenario={this.state.scenario}>
          {this.state.scenario === "ContactConfirmation" ? (
            this.getScenario()
          ) : (
            <AuthModal
              dialogStyles={dialogStyles}
              id="authentication-modal"
              isOpen={this.state.isModalOpen}
              onRequestClose={this.handleCloseModal}
              zIndex="1101"
            >
              {theme === "lightBox" && (
                <CloseButton onClick={this.handleCloseModal} />
              )}
              <ModalContent scenario={this.state.scenario}>
                {this.getScenario()}
              </ModalContent>
            </AuthModal>
          )}
        </AuthModalWrap>
        {this.getToast()}
      </ApolloProvider>
    );
  }
}

export default RouteLogin;
